
import { StandardDialog } from '@roc/ui';
import { Button, FormControl, Grid, InputLabel, Select, TextField, Typography } from '@material-ui/core';
import { Button as RocButton } from '@roc/ui';
import { makeStyles } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { formatDate, usdAmount } from './utils';
import MatDataGrid from '../matDataGrid/matDataGrid';
import { useEffect, useMemo, useState } from 'react';
import { isNil, TRACK_RECORD_MLS_DATA } from '@roc/feature-utils';


const useStyles = makeStyles((theme) =>
  createStyles({
    typography: {
      marginTop: 0,
    },
    dialogContent: {
      paddingLeft: 8,
      paddingRight: 8,
    },
    buttonContainer: {
      marginTop: 32,
    },
    button: {
      marginLeft: theme.spacing(1)
    }
  })
);

export const PublicTrackRecordLineItem = observer(
  (props: { store, isOpen, handleClose, formStore? }) => {
    const { store, isOpen, handleClose, formStore } = props;
    const classes = useStyles();

    const [entityFilter, setEntityFilter] = useState('');
    const [propertyAdressFilter, setPropertyAdressFilter] = useState('');


    const handleEntityFilterChange = event => {
      setEntityFilter(event.target.value);
    };

    const handlePropertyAdressFilterChange = (event) => {
      setPropertyAdressFilter(event.target.value);
    };

    const isEmptyArray = () => {
      return Array.isArray(filteredTrackRecords) && filteredTrackRecords.length === 0;
    }

    const filteredTrackRecords = useMemo(() => {
      return store.publicTrackRecord?.filter(record => {
        const entityMatch =
          !entityFilter ||
          (record.entityName &&
            (record.entityName.toLowerCase().includes(entityFilter.toLowerCase())
              || entityFilter.toLowerCase().includes(record.entityName.toLowerCase())));

        const propertyAddressMatch =
          !propertyAdressFilter ||
          (record.purchaseAddress &&
            (record.purchaseAddress.toLowerCase().includes(propertyAdressFilter.toLowerCase())
              || propertyAdressFilter.toLowerCase().includes(record.purchaseAddress.toLowerCase())));

        return entityMatch || propertyAddressMatch;
      });
    }, [store.publicTrackRecord, entityFilter, propertyAdressFilter]);

    const noResultsFound = useMemo(() => {
      return Array.isArray(filteredTrackRecords) && filteredTrackRecords.length === 0;
    }, [filteredTrackRecords]);

    const handleSave = async () => {
      for (let property of store.selectedPublicTrackRecords) {
        property.purchaseDate = formatDate(property.purchaseDate);
        property.saleDate = formatDate(property.saleDate);

        const borrowerId = store.currentBorrower;
        const isVerified = false;
        const recordSourceType = TRACK_RECORD_MLS_DATA;
        const isVerificationRequested = false;
        const purchaseAddress = !isNil(property?.purchaseAddress) ? `${property.purchaseAddress}, ${property.purchaseCity}, ${property.purchaseState}, ${property.purchaseZipcode}` : property?.fullAddress;

        const response = await store.savePropertyInformation({ ...property, isVerificationRequested, recordSourceType, purchaseAddress, borrowerId, isVerified }) as any;
        property.verifiedTrackRecordsId = response.data.verifiedTrackRecordsId;
      }
      handleClose();
      setEntityFilter('');
      store.publicTrackRecord = [];
      store.selectedPublicTrackRecords = [];
      store.publicTrackRecordsMatGridStore.selectedRows = [];
    }

    useEffect(() => {
      if (noResultsFound) {
        store.setHasNoTrackRecordResults(true);
      } else {
        store.setHasNoTrackRecordResults(false);
      }
    }, [noResultsFound]);

    const trackRecordsHeaders = [
      'Entity Name',
      'Buyer Name',
      'Property Address',
      'Purchase Date',
      'Purchase Amount',
      'Rehab Budget',
      'Seller Name',
      'Sale Date',
      'Sale Price',
      'Exit Strategy'
    ];

    const openPropertyDetails = () => {
      if (!isNil(formStore)) {
        formStore.form.fields.screenType.value = 'addProperty'
      }
      else {
        store.propertyFormStore.onFieldChange('screenType', 'addProperty')
      }
      store.setPropertyFormActive(true);
      store.setPropagateAddress(true);
    }

    const trackRecordsRows = filteredTrackRecords?.reduce((rows, property) => {
      rows.push({
        id: property.temporaryId,
        object: property,
        type: 'property',
        cells: [
          property.entityName ? property.entityName : '-',
          property.soldTo ? property.soldTo : '-',
          property.fullAddress ? property.fullAddress : `${property.purchaseAddress}, ${property.purchaseCity}, ${property.purchaseState}, ${property.purchaseZipcode}`,
          property.purchaseDate ? property.purchaseDate : '-',
          property.purchasePrice
            ? usdAmount({ value: property.purchasePrice })
            : '$0.00',
          property.rehabBudget
            ? usdAmount({ value: property.rehabBudget })
            : '$0.00',
          property.purchasedFrom ? property.purchasedFrom : '-',
          property.saleDate ? property.saleDate : '-',
          property.salePrice ? usdAmount({ value: property.salePrice }) : '$0.00',
          property.exitStrategy ? property.exitStrategy : '-',
        ],
        expandableContent: (
          <Grid container>
            <Grid item xs={12} style={{ marginBottom: '3rem', display: 'flex', justifyContent: 'space-between' }}>

              <Grid >
                <Grid style={{ marginRight: '4rem', marginBottom: '4rem' }}>
                  <strong>Mortgage Amount: </strong>
                  <Grid style={{ display: 'flex', flexDirection: "column" }}>
                    {property.mortgageAmount
                      ? usdAmount({ value: property.mortgageAmount })
                      : '$0.00'}
                  </Grid>
                </Grid>
                <Grid style={{ marginRight: '4rem' }}>
                  <strong>Verification Source: </strong>
                  <Grid style={{ display: 'flex', flexDirection: "column" }}>
                    {property.verificationSource ?
                      property.verificationSource
                      : 'Not Selected'}
                  </Grid>
                </Grid>
              </Grid>
              <Grid>
                <Grid style={{ marginRight: '4rem', marginBottom: '4rem' }}>
                  <strong>Source Type: </strong>
                  <Grid style={{ display: 'flex', flexDirection: "column" }}>
                    {property.recordSourceType ? property.recordSourceType : 'None'}
                  </Grid>
                </Grid>
                <Grid style={{ marginRight: '4rem' }}>
                  <strong>Lender Name: </strong>
                  <Grid style={{ display: 'flex', flexDirection: "column" }}>
                    {property.lenderName ?
                      property.lenderName
                      : 'None'}
                  </Grid>
                </Grid>
              </Grid>
              <Grid>
                <Grid style={{ marginRight: '4rem' }}>
                  <strong>Property Type: </strong>
                  <Grid style={{ display: 'flex', flexDirection: "column" }}>
                    {property.propertyType ?
                      property.propertyType
                      : 'None'}
                  </Grid>
                </Grid>
              </Grid>

            </Grid>
          </Grid>
        ),
      });

      return rows;
    }, []);

    return (
      <div className={classes.dialogContent}>
        <Grid>
          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            <Grid item xs={12}>
              <Typography variant="body2">
                This is the list of <strong>public track records</strong> associated with your current entities, select which ones you want to add.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              style={{ display: 'flex', alignItems: 'center', marginTop: '2rem', padding: '0px 0px 10px 0px' }}
            >
              <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={10}>
                  <TextField
                    variant="outlined"
                    label="Entity Name"
                    id="entityFilter"
                    value={entityFilter}
                    onChange={(e) => { handleEntityFilterChange(e); handlePropertyAdressFilterChange(e) }}
                    fullWidth
                    size='small'
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => { store.getPublicTrackRecordsByEntityName(entityFilter) }}
                    style={{ height: '100%' }}
                    fullWidth
                    size='small'
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>

            </Grid>

            <MatDataGrid
              store={store.publicTrackRecordsMatGridStore}
              isExpandable={false}
              enableSelection={true}
              enablePagination={true}
              onSelectedRowsChange={(rows) => { store.selectedPublicTrackRecords = rows.map((row) => row.object) }}
              headers={trackRecordsHeaders}
              rows={(store.publicTrackRecord && entityFilter) ? trackRecordsRows : []}
            />
            {
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-start', paddingTop: '30px' }}>
                <Typography variant="body2">
                  Your property is not listed? <strong onClick={() => {
                    store.propertyFormStore.onFieldChange('screenType', 'addProperty')
                    openPropertyDetails()
                    store.setPropertyFormActive(true);
                  }}>click here to add it now</strong>
                </Typography>
              </Grid>
            }


          </Grid>
          <Grid item xs={12} className={classes.buttonContainer}>
            <Grid container justifyContent='flex-end'>
              <RocButton
                variant='outlined'
                color='primary'
                onClick={() => { handleClose(); setEntityFilter(''); }}
                testId='removeProperty'
                className={classes.button}
                style={{ backgroundColor: 'transparent' }}
              >
                Cancel
              </RocButton>
              <RocButton
                variant="contained"
                color="primary"
                onClick={handleSave}
                testId='saveProperty'
                className={classes.button}
                disabled={store.selectedPublicTrackRecords.length === 0}
              >
                Save
              </RocButton>
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  }
);

export default PublicTrackRecordLineItem;
